import { useAppStore } from '@runtime/core/store/appStore'
import type { UserProfile } from '@runtime/core/user/userModel'
import { isAdminUserRole } from '@runtime/core/user/userRole'
import posterAccessConfig from '@runtime/core/theme/poster-access.config.json'

// Poster access config.
// Themes can remain protected with a password or explicit allowlists.

type PosterAccessThemeConfig = {
  protected: boolean
  password?: string
  allowedEmails?: string[]
  allowedPseudos?: string[]
}

type PosterAccessConfig = {
  defaultPassword: string
  themes: Record<string, PosterAccessThemeConfig>
}

type PosterAccessUser =
  | Pick<UserProfile, 'email' | 'pseudo' | 'role'>
  | null
  | undefined

type PosterAccessEntryInput = {
  id?: string
  protected?: boolean
  password?: string
  allowedEmails?: string[]
  allowedPseudos?: string[]
}

const POSTER_ACCESS = posterAccessConfig as PosterAccessConfig & {
  privateIds?: string[]
}

export const PRIVATE_POSTER_IDS = new Set(
  Array.isArray(POSTER_ACCESS.privateIds) ? POSTER_ACCESS.privateIds : [],
)

const DYNAMIC_PRIVATE_POSTER_IDS = new Set<string>()
const DYNAMIC_POSTER_ACCESS: Record<string, PosterAccessThemeConfig> = {}
const DYNAMIC_POSTER_ACCESS_TOKENS: Record<string, string> = {}

export function normalizePosterKey(value: unknown): string {
  return (value || '').toString().trim().toLowerCase()
}

export function isPosterPrivate(themeId: string | null | undefined): boolean {
  return (
    typeof themeId === 'string' &&
    (PRIVATE_POSTER_IDS.has(themeId) || DYNAMIC_PRIVATE_POSTER_IDS.has(themeId))
  )
}

export function isDynamicPosterPrivate(
  themeId: string | null | undefined,
): boolean {
  return typeof themeId === 'string' && DYNAMIC_PRIVATE_POSTER_IDS.has(themeId)
}

export function getPosterThemeAccessToken(
  themeId: string | null | undefined,
): string | null {
  if (!themeId) return null
  const token = DYNAMIC_POSTER_ACCESS_TOKENS[themeId]
  return token ? String(token) : null
}

export function getPosterThemeAccessTokens(): Array<{
  themeId: string
  accessToken: string
}> {
  return Object.entries(DYNAMIC_POSTER_ACCESS_TOKENS).map(
    ([themeId, accessToken]) => ({ themeId, accessToken }),
  )
}

export function setPosterThemeAccessToken(
  themeId: string,
  accessToken: string | null | undefined,
): void {
  const nextId = String(themeId || '').trim()
  if (!nextId) return

  const nextToken = String(accessToken || '').trim()
  if (!nextToken) {
    delete DYNAMIC_POSTER_ACCESS_TOKENS[nextId]
    return
  }

  DYNAMIC_POSTER_ACCESS_TOKENS[nextId] = nextToken
}

export function hasPosterThemeAccessToken(
  themeId: string | null | undefined,
): boolean {
  return !!getPosterThemeAccessToken(themeId)
}

export function getPosterPrivateUnlockedIds(): string[] {
  return [...useAppStore.getState().posterPrivateUnlockedIds]
}

export function isPosterPrivateUnlocked(
  themeId: string | null | undefined,
): boolean {
  if (!themeId) return false
  if (hasPosterThemeAccessToken(themeId)) return true
  if (
    useAppStore.getState().posterPrivateUnlockedAll &&
    !isDynamicPosterPrivate(themeId)
  )
    return true
  if (isDynamicPosterPrivate(themeId)) return false
  return getPosterPrivateUnlockedIds().includes(themeId)
}

export function setPosterPrivateUnlockedAll(value: boolean): void {
  useAppStore.getState().setPosterPrivateUnlockedAll(value)
}

export function addPosterPrivateUnlockedId(themeId: string): void {
  useAppStore.getState().addPosterPrivateUnlockedId(themeId)
}

export function clearPosterPrivateUnlocked() {
  useAppStore.getState().clearPosterPrivateUnlocked()
  Object.keys(DYNAMIC_POSTER_ACCESS_TOKENS).forEach((key) => {
    delete DYNAMIC_POSTER_ACCESS_TOKENS[key]
  })
}

export function isPosterAdmin(user: PosterAccessUser): boolean {
  return isAdminUserRole(user?.role)
}

export function getPosterThemeAccess(
  themeId: string | null | undefined,
): PosterAccessThemeConfig | null {
  if (!themeId) return null
  return DYNAMIC_POSTER_ACCESS[themeId] || POSTER_ACCESS.themes[themeId] || null
}

export function getPosterDefaultPassword() {
  return POSTER_ACCESS.defaultPassword
}

export function registerDynamicPosterAccess(
  entries: PosterAccessEntryInput[],
): void {
  const nextIds = new Set<string>()
  DYNAMIC_PRIVATE_POSTER_IDS.clear()

  Object.keys(DYNAMIC_POSTER_ACCESS).forEach((key) => {
    delete DYNAMIC_POSTER_ACCESS[key]
  })

  entries.forEach((entry) => {
    const id = String(entry?.id || '').trim()
    if (!id || !entry?.protected) return

    nextIds.add(id)
    DYNAMIC_PRIVATE_POSTER_IDS.add(id)
    DYNAMIC_POSTER_ACCESS[id] = {
      protected: true,
      ...(entry.password ? { password: entry.password } : {}),
      ...(Array.isArray(entry.allowedEmails) && entry.allowedEmails.length
        ? { allowedEmails: entry.allowedEmails }
        : {}),
      ...(Array.isArray(entry.allowedPseudos) && entry.allowedPseudos.length
        ? { allowedPseudos: entry.allowedPseudos }
        : {}),
    }
  })

  Object.keys(DYNAMIC_POSTER_ACCESS_TOKENS).forEach((key) => {
    if (!nextIds.has(key)) delete DYNAMIC_POSTER_ACCESS_TOKENS[key]
  })
}

/**
 * UI helper: tells if a theme should be shown as "locked" for the current user.
 */
export function isPosterThemeLocked(
  themeId: string | null | undefined,
  user: PosterAccessUser,
): boolean {
  if (!isPosterPrivate(themeId)) return false
  if (isPosterPrivateUnlocked(themeId)) return false
  if (isPosterAdmin(user)) return false
  const cfg = getPosterThemeAccess(themeId)
  if (!cfg || !cfg.protected) return false

  const email = normalizePosterKey(user && user.email)
  const pseudo = normalizePosterKey(user && user.pseudo)

  if (
    Array.isArray(cfg.allowedEmails) &&
    cfg.allowedEmails.some((e) => normalizePosterKey(e) === email)
  )
    return false
  if (
    Array.isArray(cfg.allowedPseudos) &&
    cfg.allowedPseudos.some((p) => normalizePosterKey(p) === pseudo)
  )
    return false

  return true
}

/**
 * Full access check for private posters.
 */
export function canAccessPosterTheme(
  themeId: string | null | undefined,
  user: PosterAccessUser,
): boolean {
  if (!isPosterPrivate(themeId)) return true
  if (isPosterPrivateUnlocked(themeId)) return true
  const cfg = getPosterThemeAccess(themeId)
  if (!cfg || !cfg.protected) return false

  // Admins: full access
  if (isPosterAdmin(user)) return true
  if (isDynamicPosterPrivate(themeId)) return false

  const email = normalizePosterKey(user && user.email)
  const pseudo = normalizePosterKey(user && user.pseudo)

  // Whitelists (email/pseudo)
  if (
    Array.isArray(cfg.allowedEmails) &&
    cfg.allowedEmails.some((e) => normalizePosterKey(e) === email)
  )
    return true
  if (
    Array.isArray(cfg.allowedPseudos) &&
    cfg.allowedPseudos.some((p) => normalizePosterKey(p) === pseudo)
  )
    return true

  return false
}
